import React, { useEffect, useState } from 'react';
import { getAuthorNoteDetail, deleteAuthorNote } from '../../../api';
import CommentList from './CommentList';
import { useIsMobile } from '../../../hooks/useIsMobile';

interface AuthorNoteDetailProps {
    noteId: string;
    isOwner: boolean;
    onBack: () => void;
    onEdit: (data: any) => void;
    variant?: 'mypage' | 'authorDetail';
}

const AuthorNoteDetail = ({ noteId, isOwner, onBack, onEdit, variant = 'authorDetail' }: AuthorNoteDetailProps) => {
    const isMypage = variant === 'mypage';
    const isMobile = useIsMobile();
    const [note, setNote] = useState<any>(null);

    useEffect(() => {
        fetchDetail();
    }, [noteId]);

    const fetchDetail = async () => {
        try {
            const response = await getAuthorNoteDetail(noteId);
            setNote(response);
        } catch (error) {
            console.error("Failed to fetch detail", error);
            alert("게시물을 불러올 수 없습니다.");
            onBack();
        }
    };

    const handleDelete = async () => {
        if (window.confirm("정말 삭제하시겠습니까?")) {
            try {
                await deleteAuthorNote(noteId);
                alert("삭제되었습니다.");
                onBack();
            } catch (error) {
                console.error("Failed to delete", error);
                alert("삭제에 실패했습니다.");
            }
        }
    };

    const formatDate = (dateStr: string) => {
        try {
            return new Date(dateStr).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch {
            return dateStr;
        }
    };

    if (!note) return <div className="py-20 text-center text-[#A1A1A1] text-[14px]">불러오는 중...</div>;

    if (isMypage) {
        // 모바일: 목록으로(회색) + 글 영역(흰색) + 수정/삭제(회색)
        if (isMobile) {
            return (
                <div className="w-full flex flex-col">
                    {/* 목록으로 — 회색 */}
                    <div className="bg-[#f5f5f5] px-4 pt-4 pb-3">
                        <button
                            onClick={onBack}
                            className="flex items-center gap-1.5 text-[#4b5563] text-[13px] font-medium hover:opacity-70 transition-opacity"
                        >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                            목록으로
                        </button>
                    </div>

                    {/* 글 영역 — 흰색 */}
                    <div className="bg-white px-4 py-5">
                        <div className="flex items-center flex-wrap gap-2 mb-2">
                            <span className="px-2.5 py-0.5 bg-[#f0fdf4] text-[#0d894f] text-[11px] font-medium rounded-full">
                                {note.categoryName || note.category || '영감 노트'}
                            </span>
                            <span className="text-[#6b7280] text-[12px]">
                                {note.createdAt ? formatDate(note.createdAt) : ''}
                            </span>
                        </div>

                        <h1 className="font-bold text-[#111827] text-[18px] leading-[1.4] mb-3 word-break-keep">
                            {note.title}
                        </h1>

                        <div className="flex items-center gap-4 text-[#6b7280] text-[12px] mb-3">
                            <div className="flex items-center gap-1">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                </svg>
                                <span>{note.commentCount || 0}</span>
                            </div>
                        </div>

                        <div className="border-t border-[#e5e7eb] mb-4" />

                        <div
                            className="author-note-content text-[#374151] text-[14px] leading-[24px] min-h-[150px]"
                            dangerouslySetInnerHTML={{ __html: note.content }}
                        />
                    </div>

                    {/* 수정/삭제 — 회색 */}
                    {isOwner && (
                        <div className="bg-[#f5f5f5] flex justify-end gap-2 px-4 py-3">
                            <button
                                onClick={() => onEdit(note)}
                                className="flex h-[30px] px-4 justify-center items-center rounded-[30px] bg-[#eafbf3] text-[#19cf7a] font-semibold text-[12px] hover:opacity-80 transition-opacity"
                            >
                                수정
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex h-[30px] px-4 justify-center items-center rounded-[30px] bg-[#fff2f2] text-[#e53935] font-semibold text-[12px] hover:opacity-80 transition-opacity"
                            >
                                삭제
                            </button>
                        </div>
                    )}

                    <div className="mt-2">
                        <CommentList noteId={noteId} onCommentCountChange={(count) => setNote((prev: any) => prev ? { ...prev, commentCount: count } : null)} />
                    </div>

                    <style>{`
                        .author-note-content img { max-width: 100%; height: auto; border-radius: 6px; margin: 12px 0; }
                        .author-note-content p { margin-bottom: 1.2em; word-break: keep-all; overflow-wrap: break-word; }
                        .author-note-content figure { margin: 12px 0; }
                        .author-note-content h2, .author-note-content h3 { font-weight: 700; color: #1f2937; margin-bottom: 0.6em; }
                    `}</style>
                </div>
            );
        }

        // 데스크탑: 회색 배경 + 흰 카드
        return (
            <div className="w-full flex flex-col">
                {/* 회색 배경 섹션 */}
                <div className="bg-[#f5f5f5] flex flex-col px-[31px] pt-[33px] pb-[40px]">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-1.5 text-[#1f2937] text-[16px] font-medium mb-6 self-start hover:opacity-70 transition-opacity"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                        목록으로
                    </button>

                    <div className="bg-white rounded-[8px] px-[34px] py-[32px]">
                        <div className="flex items-center flex-wrap gap-2 mb-3">
                            <span className="px-2.5 py-0.5 bg-[#f0fdf4] text-[#0d894f] text-[12px] font-medium rounded-full">
                                {note.categoryName || note.category || '영감 노트'}
                            </span>
                            <span className="text-[#6b7280] text-[14px]">
                                {note.createdAt ? formatDate(note.createdAt) : ''}
                            </span>
                        </div>

                        <h1 className="font-bold text-[#111827] text-[32px] leading-[1.45] mb-3 word-break-keep">
                            {note.title}
                        </h1>

                        <div className="flex items-center gap-4 text-[#6b7280] text-[14px] mb-4">
                            <div className="flex items-center gap-1">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                </svg>
                                <span>{note.commentCount || 0}</span>
                            </div>
                        </div>

                        <div className="border-t border-[#e5e7eb] mb-8" />

                        <div
                            className="author-note-content text-[#374151] text-[20px] leading-[32px] min-h-[150px]"
                            dangerouslySetInnerHTML={{ __html: note.content }}
                        />
                    </div>

                    {isOwner && (
                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => onEdit(note)}
                                className="flex h-[46px] px-[37px] justify-center items-center rounded-[30px] bg-[#eafbf3] text-[#19cf7a] font-semibold text-[16px] hover:opacity-80 transition-opacity"
                            >
                                수정
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex h-[46px] px-[37px] justify-center items-center rounded-[30px] bg-[#fff2f2] text-[#e53935] font-semibold text-[16px] hover:opacity-80 transition-opacity"
                            >
                                삭제
                            </button>
                        </div>
                    )}
                </div>

                {/* 댓글 섹션 */}
                <div className="mt-4">
                    <CommentList noteId={noteId} onCommentCountChange={(count) => setNote((prev: any) => prev ? { ...prev, commentCount: count } : null)} />
                </div>

                <style>{`
                    .author-note-content img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 8px;
                        margin: 16px 0;
                    }
                    .author-note-content p { margin-bottom: 1.2em; word-break: keep-all; overflow-wrap: break-word; }
                    .author-note-content figure { margin: 16px 0; }
                    .author-note-content h2, .author-note-content h3 { font-weight: 700; color: #1f2937; margin-bottom: 0.6em; }
                `}</style>
            </div>
        );
    }

    // authorDetail variant
    return (
        <div className="w-full flex flex-col">
            <div className="bg-white border border-[#e5e7eb] rounded-[8px] flex flex-col">
                <button
                    onClick={onBack}
                    className="flex items-center gap-1.5 text-[#767676] hover:text-[#2F2F2F] text-[14px] font-medium transition-colors self-start px-6 pt-5"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                    목록으로
                </button>

                <div className="px-4 py-5 md:px-10 md:py-10">
                    <div className="flex items-center flex-wrap gap-2 mb-3">
                        <span className="px-2.5 py-0.5 bg-[#f0fdf4] text-[#0d894f] text-[11px] font-medium rounded-full">
                            {note.categoryName || note.category || '영감 노트'}
                        </span>
                        <span className="text-[#6b7280] text-[12px]">
                            {note.createdAt ? formatDate(note.createdAt) : ''}
                        </span>
                    </div>

                    <h1 className="font-bold text-[#1f2937] text-[17px] md:text-[28px] leading-[1.45] mb-3 word-break-keep">
                        {note.title}
                    </h1>

                    <div className="flex items-center gap-4 text-[#6b7280] text-[12px] md:text-[14px] mb-4">
                        <div className="flex items-center gap-1">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                            </svg>
                            <span>{note.commentCount || 0}</span>
                        </div>
                    </div>

                    <div className="border-t border-[#e5e7eb] mb-5 md:mb-8" />

                    <div
                        className="author-note-content text-[#4b5563] text-[14px] md:text-[18px] leading-[32px] min-h-[150px]"
                        dangerouslySetInnerHTML={{ __html: note.content }}
                    />

                    {isOwner && (
                        <div className="flex justify-end gap-2 mt-8">
                            <button
                                onClick={() => onEdit(note)}
                                className="flex h-[40px] px-6 justify-center items-center rounded-full bg-[#eafbf3] text-[#19cf7a] font-bold text-[13px] md:text-[15px] hover:opacity-80 transition-opacity"
                            >
                                수정
                            </button>
                            <button
                                onClick={handleDelete}
                                className="flex h-[40px] px-6 justify-center items-center rounded-full bg-[#fff2f2] text-[#e53935] font-bold text-[13px] md:text-[15px] hover:opacity-80 transition-opacity"
                            >
                                삭제
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4">
                <CommentList noteId={noteId} onCommentCountChange={(count) => setNote((prev: any) => prev ? { ...prev, commentCount: count } : null)} />
            </div>

            <style>{`
                .author-note-content img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 8px;
                    margin: 16px 0;
                }
                .author-note-content p { margin-bottom: 1.2em; word-break: keep-all; overflow-wrap: break-word; }
                .author-note-content figure { margin: 16px 0; }
                .author-note-content h2, .author-note-content h3 { font-weight: 700; color: #1f2937; margin-bottom: 0.6em; }
                @media (max-width: 639px) {
                    .author-note-content img { border-radius: 6px; margin: 12px 0; }
                }
            `}</style>
        </div>
    );
};

export default AuthorNoteDetail;
